import React, { useMemo } from 'react';
import * as Yup from 'yup';

import { GlobalAccountRoleType, GlobalAccountUpdateRequest, ServiceCadence } from '@ndvr/customer-api';

import { PrimaryAdvisorSelector } from 'modules/FormModules';
import { getPersonByRole, getPersonName, titleCase, useHouseholdRouteType, useRequiredParams } from 'utils';
import {
    AccordionSection,
    AccordionSectionKeys,
    DataGrid,
    Form,
    FormInput,
    FormSelect,
    MailTo,
    notBlankStringValidator,
    PhoneNumber,
    SaveBar,
    TextLink,
} from 'widgets';

import { useCommandCenterHouseholdProfile, useUpdateGlobalAccount } from './household-queries';

const ACCORDION_MAX_WIDTH = '762px';
const LABEL_MIN_WIDTH = '200px';
const VALUE_MIN_WIDTH = '350px';

const formSchema = Yup.object().shape({
    globalAccountNamePlain: notBlankStringValidator,
    serviceCadence: Yup.mixed<ServiceCadence>()
        .oneOf(Object.values(ServiceCadence), 'Service cadence is required')
        .required(),
});

export const DetailsTab = () => {
    const { globalAccountGUID } = useRequiredParams(['globalAccountGUID']);
    const { data: householdData, isPending } = useCommandCenterHouseholdProfile(globalAccountGUID);
    const { mutate: updateGlobalAccount } = useUpdateGlobalAccount(globalAccountGUID);
    const { householdTypeLabel } = useHouseholdRouteType();

    const primaryAccountManager = getPersonByRole(
        householdData?.personOrgFinancialProfiles,
        GlobalAccountRoleType.PRIMARY_ACCOUNT_MANAGER,
    );

    const defaultValues = useMemo(() => {
        return {
            globalAccountGUID,
            primaryAdvisorGUID: householdData?.globalAccount.primaryAdvisorGUID,
            globalAccountNamePlain: householdData?.globalAccount.globalAccountNamePlain,
            serviceCadence: householdData?.globalAccount.serviceCadence,
        };
    }, [householdData, globalAccountGUID]);

    // The update replaces every column, so carry over the fields this form does not edit.
    // An empty advisor keeps the current one: the selector empties the field for inactive advisors.
    const onSubmit = (formData: GlobalAccountUpdateRequest) =>
        updateGlobalAccount({
            ...formData,
            primaryAdvisorGUID: formData.primaryAdvisorGUID || householdData?.globalAccount.primaryAdvisorGUID,
            householdType: householdData?.globalAccount.householdType,
            ffEmployeePersonOrgGUID: householdData?.globalAccount.ffEmployeePersonOrgGUID,
            ffAffiliationLevel: householdData?.globalAccount.ffAffiliationLevel,
        });

    const principalDatagridValues = [
        {
            label: 'Principal Account Manager',
            value: (
                <TextLink to={`/person/${primaryAccountManager?.profile.personOrgGUID}`}>
                    {getPersonName(primaryAccountManager?.profile.personOrg)}
                </TextLink>
            ),
        },
        {
            label: 'Primary email',
            value: <MailTo address={primaryAccountManager?.profile.personOrg.primaryEmail?.plainText} />,
        },
        ...(primaryAccountManager?.profile.personOrg.otherEmail?.plainText
            ? [
                  {
                      label: 'Other email',
                      value: <MailTo address={primaryAccountManager?.profile.personOrg.otherEmail?.plainText} />,
                  },
              ]
            : []),
        {
            label: 'Personal phone',
            value: <PhoneNumber phoneNumber={primaryAccountManager?.profile.personOrg.personalPhone?.plainText} />,
        },
        ...(primaryAccountManager?.profile.personOrg.businessPhone?.plainText
            ? [
                  {
                      label: 'Business phone',
                      value: (
                          <PhoneNumber
                              phoneNumber={primaryAccountManager?.profile.personOrg.businessPhone?.plainText}
                          />
                      ),
                  },
              ]
            : []),
    ];

    return (
        <div className="flex flex-col gap-4">
            <Form
                labelWidth="200px"
                defaultValues={defaultValues}
                onSubmit={onSubmit}
                isLoading={isPending}
                formSchema={formSchema}
            >
                <div className="flex flex-col gap-4">
                    <AccordionSection
                        title={householdTypeLabel + ' Details'}
                        helperText="3 Properties"
                        maxWidth={ACCORDION_MAX_WIDTH}
                        accordionKey={AccordionSectionKeys.householdDetailsAccountDetails}
                        $background="white"
                    >
                        <DataGrid
                            labelMinWidth={LABEL_MIN_WIDTH}
                            valueMinWidth={VALUE_MIN_WIDTH}
                            values={[
                                {
                                    label: 'Name',
                                    value: (
                                        <FormInput
                                            hideLabel
                                            name="globalAccountNamePlain"
                                            label="Name"
                                            placeholder="Name of the Household"
                                        />
                                    ),
                                },
                                {
                                    label: 'Primary Advisor',
                                    value: (
                                        <PrimaryAdvisorSelector
                                            hideLabel
                                            tenantGUID={householdData?.globalAccount.tenantGUID}
                                        />
                                    ),
                                },
                                {
                                    label: 'Service Cadence',
                                    value: (
                                        <FormSelect name="serviceCadence" hideLabel label="Service Cadence">
                                            {Object.values(ServiceCadence).map((cadence) => (
                                                <option value={cadence} key={cadence}>
                                                    {titleCase(cadence)}
                                                </option>
                                            ))}
                                        </FormSelect>
                                    ),
                                },
                            ]}
                        />
                    </AccordionSection>
                    <AccordionSection
                        title="Principal"
                        helperText={principalDatagridValues.length + ' Properties'}
                        maxWidth={ACCORDION_MAX_WIDTH}
                        accordionKey={AccordionSectionKeys.householdDetailsPrincipal}
                        $background="white"
                    >
                        <DataGrid
                            labelMinWidth={LABEL_MIN_WIDTH}
                            valueMinWidth={VALUE_MIN_WIDTH}
                            values={principalDatagridValues}
                        />
                    </AccordionSection>
                </div>
                <SaveBar />
            </Form>
        </div>
    );
};
